import type { SbWorkspaceReadiness } from '../engine/testMode.types'
import { SESSION_KEY_ACTIVE_WORKSPACE, SESSION_KEY_PENDING_WORKSPACE } from '../engine/parseUrl'
import type { EngineManager, SyncState } from '../engine/types'
import type { BrowserHttpErrorEntry } from '@secureblob/sdk/browserHttpErrors'
import { getRecentBrowserHttpErrors } from '@secureblob/sdk/browserHttpErrors'
import type { BrowserRuntimeErrorEntry } from '@secureblob/sdk/browserRuntimeErrors'
import { getRecentBrowserRuntimeErrors } from '@secureblob/sdk/browserRuntimeErrors'
import type { McpToolCallEntry } from '@secureblob/sdk/browserMcpTraffic'
import { getRecentMcpToolCalls } from '@secureblob/sdk/browserMcpTraffic'
import type { SqlQueryDebugSnapshot } from '@secureblob/sdk/browserMcpTraffic'
import { getSqlQueryDebugSnapshot } from '@secureblob/sdk/browserMcpTraffic'

export interface RuntimeDebugSnapshot {
    capturedAt: string
    /** Origin + pathname only. Query and hash are redacted. */
    url: string
    uxState: string
    online: boolean
    isPlaywright: boolean
    ephemeralMode: boolean
    localServerMode: boolean
    identity: {
        hasIdentity: boolean
        vaultExists: boolean
        vaultUnlockMethod: string | null
        displayName: string | null
        emoji: string | null
    }
    workspaceReadiness: SbWorkspaceReadiness
    activeWorkspace: {
        blobId: string
        label: string | null
        emoji: string | null
        canWrite: boolean
    } | null
    suspendedWorkspaces: Array<{
        blobId: string
        label: string
        emoji: string | null
    }>
    workspaceDiscovery: {
        activeWorkspaceBlobId: string | null
        pendingWorkspaceBlobId: string | null
        guestCapBlobIds: string[]
    }
    registry: Array<{
        blobId: string
        label: string
        emoji: string | null
        canWrite: boolean
    }>
    mcpConnections: Array<{
        label: string
        serverUrl: string | null
        connected: boolean | null
    }>
    mcpToolCalls: McpToolCallEntry[]
    /** §205: Last MCP SQL tool execution provenance (path/source/lane plus query or execute result metadata). */
    sqlQueryDebugSnapshot: SqlQueryDebugSnapshot | null
    httpErrors: BrowserHttpErrorEntry[]
    /** §389: Unhandled browser exceptions and rejected promises. */
    runtimeErrors: BrowserRuntimeErrorEntry[]
    storage: {
        localStorageKeys: string[]
        sessionStorageKeys: string[]
        indexedDbNames: string[]
    }
}

export interface BuildRuntimeDebugSnapshotOptions {
    workspaceReadiness?: SbWorkspaceReadiness | null
    localServerMode?: boolean
}

export type RuntimeDebugUxState =
    | 'welcome'
    | 'autoUnlock'
    | 'unlockPrompt'
    | 'readWorkspace'
    | 'myWorkspace'
    | 'workspaceError'
    | 'offlineLanding'
    | 'unknown'

function readRuntimeDebugIdentity(identityProvider: IdentityLike | undefined | null): {
    hasIdentity: boolean
    currentIdentity: IdentityLike['currentIdentity']
    vaultExists: boolean
    vaultUnlockMethod: string | null
} {
    try {
        const currentIdentity = identityProvider?.getIdentity?.() ?? identityProvider?.currentIdentity ?? null
        return {
            hasIdentity: !!(identityProvider?.hasIdentity?.() || currentIdentity),
            currentIdentity,
            vaultExists: !!identityProvider?.vaultExists,
            vaultUnlockMethod: identityProvider?.vaultUnlockMethod ?? null,
        }
    } catch {
        return {
            hasIdentity: false,
            currentIdentity: null,
            vaultExists: false,
            vaultUnlockMethod: null,
        }
    }
}

export function detectRuntimeDebugUxState(engineManager: EngineManager): RuntimeDebugUxState {
    const { hasIdentity: identityLoaded, vaultExists, vaultUnlockMethod } = readRuntimeDebugIdentity(engineManager.identity as IdentityLike)

    const guestCap = Object.keys(sessionStorage).some(key => key.startsWith('sb:guest-cap:'))
    if (identityLoaded) return 'myWorkspace'
    if (guestCap) return 'readWorkspace'
    if (vaultExists) {
        const unlockPromptMounted = typeof document !== 'undefined'
            && document.querySelector('.unlock-prompt-backdrop') !== null
        if (unlockPromptMounted) {
            return 'unlockPrompt'
        }

        // During bootstrap a WebAuthn vault can still be attempting silent
        // trusted-device recovery before the unlock prompt mounts.
        if (vaultUnlockMethod?.startsWith('webauthn')) return 'autoUnlock'
        return 'unlockPrompt'
    }
    if (!navigator.onLine) return 'offlineLanding'
    return 'welcome'
}

function serializeSyncError(
    error: SyncState['lastSyncError'] | undefined,
): SbWorkspaceReadiness['lastSyncError'] {
    if (!error) return null
    return {
        code: error.code,
        message: error.message,
    }
}

export function buildWorkspaceReadiness(
    engineManager: EngineManager,
    syncStates: Map<string, SyncState>,
): SbWorkspaceReadiness {
    const defaultConnectivity = navigator.onLine ? 'connected' : 'offline'
    const workspace = engineManager.getActiveWorkspace()

    const readinessBlobId = workspace?.blobId ?? sessionStorage.getItem(SESSION_KEY_ACTIVE_WORKSPACE)
    const suspendedState = readinessBlobId
        ? engineManager.getSuspendedWorkspaces().find(entry => entry.blobId === readinessBlobId)?.state
        : null

    if (!readinessBlobId) {
        return {
            blobId: null,
            canWrite: false,
            syncing: false,
            synced: false,
            online: navigator.onLine,
            connectivity: defaultConnectivity,
            pendingChanges: 0,
            lastSyncError: null,
            localHead: null,
            remoteHead: null,
            conflictPaths: [],
        }
    }

    const state = syncStates.get(readinessBlobId) ?? workspace?.state ?? suspendedState ?? undefined
    const readinessCanWrite = workspace?.canWrite
        ?? (state
            ? engineManager.getRegistry().find(entry => entry.blobId === readinessBlobId)?.canWrite
            : false)
        ?? false

    return {
        blobId: readinessBlobId,
        canWrite: readinessCanWrite,
        syncing: state?.syncing ?? false,
        synced: state?.synced ?? false,
        online: state?.online ?? navigator.onLine,
        connectivity: state?.connectivity ?? defaultConnectivity,
        pendingChanges: state?.pendingChanges ?? 0,
        lastSyncError: serializeSyncError(state?.lastSyncError),
        localHead: state?.localHead ?? null,
        remoteHead: state?.remoteHead ?? null,
        conflictPaths: [...(state?.conflictPaths ?? [])],
    }
}

function getStorageKeys(storage: Storage): string[] {
    const keys: string[] = []
    for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i)
        if (key) keys.push(key)
    }
    keys.sort((a, b) => a.localeCompare(b))
    return keys
}

function getGuestCapBlobIds(): string[] {
    const blobIds: string[] = []
    for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i)
        if (key?.startsWith('sb:guest-cap:')) {
            blobIds.push(key.replace('sb:guest-cap:', ''))
        }
    }
    blobIds.sort((a, b) => a.localeCompare(b))
    return blobIds
}

function getPendingWorkspaceBlobId(): string | null {
    const raw = sessionStorage.getItem(SESSION_KEY_PENDING_WORKSPACE)
    if (!raw) return null

    try {
        const parsed = JSON.parse(raw)
        if (typeof parsed === 'string') return parsed
        if (
            typeof parsed === 'object'
            && parsed !== null
            && 'blobId' in parsed
            && typeof parsed.blobId === 'string'
        ) {
            return parsed.blobId
        }
    } catch {
        return null
    }

    return null
}

async function getIndexedDbNames(): Promise<string[]> {
    if (typeof indexedDB === 'undefined' || typeof indexedDB.databases !== 'function') {
        return []
    }

    try {
        const databases = await indexedDB.databases()
        return databases
            .map(database => database.name ?? '')
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b))
    } catch {
        return []
    }
}

type IdentityLike = {
    hasIdentity?: () => boolean
    getIdentity?: () => { name?: string; emoji?: string } | null
    currentIdentity?: { name?: string; emoji?: string } | null
    vaultExists?: boolean
    vaultUnlockMethod?: string | null
}

type WorkspaceLike = {
    blobId: string
    label?: string
    emoji?: string | null
    canWrite: boolean
    state?: SyncState
}

type McpConnectionLike = {
    label?: string
    serverUrl?: string
    url?: string | URL
    connected?: boolean
    status?: string
}

export async function buildRuntimeDebugSnapshot(
    engineManager: EngineManager,
    syncStates: Map<string, SyncState>,
    options: BuildRuntimeDebugSnapshotOptions = {},
): Promise<RuntimeDebugSnapshot> {
    const isPlaywright = (navigator as { webdriver?: boolean }).webdriver === true
    const ephemeralMode = !!(window as { __e2eStorageSecret?: string }).__e2eStorageSecret

    let identity: RuntimeDebugSnapshot['identity'] = {
        hasIdentity: false,
        vaultExists: false,
        vaultUnlockMethod: null,
        displayName: null,
        emoji: null,
    }

    try {
        const identityProvider = engineManager.identity as IdentityLike
        const { hasIdentity, currentIdentity, vaultExists, vaultUnlockMethod } = readRuntimeDebugIdentity(identityProvider)
        identity = {
            hasIdentity,
            vaultExists,
            vaultUnlockMethod,
            displayName: currentIdentity?.name ?? null,
            emoji: currentIdentity?.emoji ?? null,
        }
    } catch {
        // Best-effort snapshot only.
    }

    const workspaceReadiness = options.workspaceReadiness
        ?? buildWorkspaceReadiness(engineManager, syncStates)

    const activeWorkspace = engineManager.getActiveWorkspace() as WorkspaceLike | null
    const activeWorkspaceInfo: RuntimeDebugSnapshot['activeWorkspace'] = activeWorkspace
        ? {
            blobId: activeWorkspace.blobId,
            label: activeWorkspace.label ?? null,
            emoji: activeWorkspace.emoji ?? null,
            canWrite: activeWorkspace.canWrite,
        }
        : null

    const suspendedWorkspaces: RuntimeDebugSnapshot['suspendedWorkspaces'] = engineManager
        .getSuspendedWorkspaces()
        .map(entry => ({
            blobId: entry.blobId,
            label: entry.label,
            emoji: entry.emoji ?? null,
        }))

    const registry: RuntimeDebugSnapshot['registry'] = engineManager.getRegistry().map(entry => ({
        blobId: entry.blobId,
        label: entry.label,
        emoji: entry.emoji ?? null,
        canWrite: entry.canWrite ?? false,
    }))

    const mcpConnections: RuntimeDebugSnapshot['mcpConnections'] = engineManager
        .getMcpConnections()
        .map(connection => {
            const runtimeConnection = connection as McpConnectionLike
            const serverUrl = typeof runtimeConnection.serverUrl === 'string'
                ? runtimeConnection.serverUrl
                : typeof runtimeConnection.url === 'string'
                    ? runtimeConnection.url
                    : runtimeConnection.url instanceof URL
                        ? runtimeConnection.url.toString()
                        : null

            const connected = typeof runtimeConnection.connected === 'boolean'
                ? runtimeConnection.connected
                : typeof runtimeConnection.status === 'string'
                    ? ['connected', 'ready'].includes(runtimeConnection.status)
                    : null

            return {
                label: runtimeConnection.label ?? '(unnamed connection)',
                serverUrl,
                connected,
            }
        })

    return {
        capturedAt: new Date().toISOString(),
        url: `${window.location.origin}${window.location.pathname}`,
        uxState: detectRuntimeDebugUxState(engineManager),
        online: navigator.onLine,
        isPlaywright,
        ephemeralMode,
        localServerMode: options.localServerMode ?? false,
        identity,
        workspaceReadiness,
        activeWorkspace: activeWorkspaceInfo,
        suspendedWorkspaces,
        workspaceDiscovery: {
            activeWorkspaceBlobId: activeWorkspace?.blobId ?? sessionStorage.getItem(SESSION_KEY_ACTIVE_WORKSPACE),
            pendingWorkspaceBlobId: getPendingWorkspaceBlobId(),
            guestCapBlobIds: getGuestCapBlobIds(),
        },
        registry,
        mcpConnections,
        mcpToolCalls: getRecentMcpToolCalls(),
        sqlQueryDebugSnapshot: getSqlQueryDebugSnapshot(),
        httpErrors: getRecentBrowserHttpErrors(),
        runtimeErrors: getRecentBrowserRuntimeErrors(),
        storage: {
            localStorageKeys: getStorageKeys(localStorage),
            sessionStorageKeys: getStorageKeys(sessionStorage),
            indexedDbNames: await getIndexedDbNames(),
        },
    }
}
